import type { RelatedObject } from 'api/events/endpoints/getEventRelatedObjects';

// The display rules for one related object, kept apart from the component so
// they are unit-testable without pulling the endpoint -- and through it
// services/api -- into the test environment (#1083).

/** The object's own name. A flow step is named by its flow and the step, not by the child placed there. */
export const objectName = (object: RelatedObject): string => {
  switch (object.kind) {
    case 'status_change_trigger':
      return object.trigger_name ?? '—';
    case 'flow_node_wait':
    case 'flow_node_completion':
      // flow_node_label is null on placements created before it was populated,
      // so the step id is the fallback that always identifies the step.
      return `${object.flow_definition_name ?? '—'} › ${object.flow_node_label ?? object.flow_node_id ?? '—'}`;
    default:
      return object.node_name ?? '—';
  }
};

export const objectType = (object: RelatedObject): string => {
  switch (object.kind) {
    case 'status_change_trigger':
      return 'Trigger';
    case 'flow_node_wait':
    case 'flow_node_completion':
      return 'Flow step';
    default:
      return 'Node definition';
  }
};

/**
 * How the object uses the event: the relationship and its mechanism, and
 * nothing about how it is configured.
 *
 * The read carries no auto-submit/auto-clear, status, precondition, scheduling
 * window, runtime-limit threshold or trigger scope — see RelatedObject for why
 * those cannot be answered correctly by a definition-level read. What is left
 * is knowable completely from `kind` alone, which is the point: every string
 * here is true of every row it is shown for.
 */
export const usage = (object: RelatedObject): string => {
  switch (object.kind) {
    case 'node_wait':
    case 'flow_node_wait':
      return 'Waits';
    case 'node_completion':
    case 'flow_node_completion':
      return 'Raises on completion';
    case 'runtime_limit':
      return 'Raises on runtime limit';
    case 'status_change_trigger':
      return 'Raises on status change';
    default:
      return '';
  }
};

/** The child a flow step places, which is not the step's own identity but is worth naming. */
export const runsChild = (object: RelatedObject): string | null => {
  if (object.kind !== 'flow_node_wait' && object.kind !== 'flow_node_completion') return null;
  if (!object.node_name) return null;
  return object.child_zone_name && object.child_zone_name !== object.zone_name
    ? `runs ${object.node_name} (${object.child_zone_name})`
    : `runs ${object.node_name}`;
};

/**
 * Identifies a row.
 *
 * No single id is unique across the list, and the fields that would
 * disambiguate are configuration the read does not carry: one definition can
 * wait on an event in several scheduling windows, and a flow can hold two
 * entries for one event on one step. Both arrive as identical rows. The index
 * is therefore load-bearing, not a belt-and-braces suffix — without it those
 * rows collide and React orphans one (cf. #1178).
 *
 * Presenting such rows as a single entry is the real fix and needs either the
 * window back or a count from the server; until then the duplicate is visible,
 * which is the honest failure.
 */
export const rowKey = (object: RelatedObject, index: number): string =>
  [object.kind, object.flow_definition_id, object.flow_node_id, object.node_definition_id, object.trigger_id, index].join(':');

export const byTypeThenName = (a: RelatedObject, b: RelatedObject): number =>
  objectType(a).localeCompare(objectType(b)) || objectName(a).localeCompare(objectName(b));
